// Mapeo formulario <-> credencial tipo "api" del credential_service.
// Contrato (shared_core ApiCredential): base_url, auth_params (discriminado por
// auth_type: none | api_key | bearer_token | oauth2), headers, timeout.
// Se mantiene puro para poder testearlo sin la UI.
import { rowsToDict, dictToRows } from "../../lib/keyValueRows";

export const AUTH_TYPES = [
  { value: "none", label: "None (public API)" },
  { value: "bearer_token", label: "Bearer token" },
  { value: "api_key", label: "API key in header" },
  { value: "oauth2", label: "OAuth 2.0 (access token)" },
];

// Campos secretos por tipo: si en una edición quedan vacíos, se conserva el
// valor guardado (el formulario nunca vuelve a mostrar un secreto).
const SECRET_FIELDS = {
  api_key: ["api_key"],
  bearer_token: ["token"],
  oauth2: ["client_secret", "access_token", "refresh_token"],
  none: [],
};

export const emptyApiCredentialForm = () => ({
  name: "",
  description: "",
  base_url: "",
  timeout: 30,
  auth_type: "none",
  api_key: "",
  header_name: "X-API-Key",
  token: "",
  client_id: "",
  client_secret: "",
  access_token: "",
  refresh_token: "",
  token_url: "",
  headers: [],
});

// Estado del formulario a partir de una credencial existente (GET completo).
// Los secretos se dejan vacíos a propósito.
export const formFromApiCredential = (cred) => {
  const auth = cred?.auth_params || {};
  return {
    ...emptyApiCredentialForm(),
    name: cred?.name || "",
    description: cred?.description || "",
    base_url: cred?.base_url || "",
    timeout: cred?.timeout || 30,
    auth_type: auth.auth_type || "none",
    header_name: auth.header_name || "X-API-Key",
    client_id: auth.client_id || "",
    token_url: auth.token_url || "",
    headers: dictToRows(cred?.headers),
  };
};

// Auth guardada cuyos secretos se pueden conservar: solo si se está editando y
// no cambió el tipo de autenticación (un token bearer no sirve como api_key).
export const reusableSavedAuth = (form, existing) =>
  existing?.auth_params?.auth_type === form.auth_type ? existing.auth_params : null;

const buildAuthParams = (form, existing) => {
  const saved = reusableSavedAuth(form, existing) || {};
  const secret = (field) => form[field] || saved[field] || "";
  switch (form.auth_type) {
    case "api_key":
      return { auth_type: "api_key", api_key: secret("api_key"), header_name: form.header_name || "X-API-Key" };
    case "bearer_token":
      return { auth_type: "bearer_token", token: secret("token") };
    case "oauth2":
      return {
        auth_type: "oauth2",
        client_id: form.client_id,
        client_secret: secret("client_secret"),
        access_token: secret("access_token") || null,
        refresh_token: secret("refresh_token") || null,
        token_url: form.token_url || null,
      };
    default:
      return { auth_type: "none" };
  }
};

// Devuelve el mensaje de error de validación, o null si el formulario es válido.
export const validateApiCredentialForm = (form, existing = null) => {
  if (!form.name.trim()) return "Name is required";
  if (!/^https?:\/\/\S+$/i.test(form.base_url.trim())) return "Base URL must start with http:// or https://";
  const saved = reusableSavedAuth(form, existing) || {};
  const missing = (SECRET_FIELDS[form.auth_type] || [])
    .filter((f) => f !== "access_token" && f !== "refresh_token")
    .find((f) => !form[f] && !saved[f]);
  if (missing) return `${missing.replace("_", " ")} is required`;
  if (form.auth_type === "oauth2" && !form.client_id.trim()) return "client id is required";
  return null;
};

// Payload para POST /credential (create) o PUT /credential/api/{id} (update).
// El PUT valida el objeto completo, por eso en edición se arrastran
// account_id y created_by_user de la credencial existente.
export const buildApiCredentialPayload = (form, existing = null) => {
  const payload = {
    credential_type: "api",
    name: form.name.trim(),
    description: form.description?.trim() || null,
    base_url: form.base_url.trim(),
    auth_params: buildAuthParams(form, existing),
    headers: rowsToDict(form.headers),
    timeout: Number(form.timeout) > 0 ? Number(form.timeout) : 30,
  };
  if (existing) {
    payload.account_id = existing.account_id;
    payload.created_by_user = existing.created_by_user;
  }
  return payload;
};
